import { useCallback, useState } from 'react';

import { api, type ContractorWeek } from './api';
import { RevenueArea } from './components/Charts';
import {
  Card,
  CardHeader,
  Cell,
  ExportButton,
  Kpi,
  Row,
  SearchBar,
  Spinner,
  Table,
} from './components/Ui';
import { useAsyncData } from './hooks/useAsyncData';
import { useSortedRows } from './hooks/useSortedRows';
import { hours, marginPercent, money, moneyCompact, rate, shortDate } from './format';
import { Shell, type ShellProps } from './Shell';

export function Contractor360(props: ShellProps & { contractorId: string }) {
  const { contractorId } = props;
  const [search, setSearch] = useState('');
  const [showRateForm, setShowRateForm] = useState(false);
  const [rateForm, setRateForm] = useState({
    effectiveFrom: '',
    payRate: '',
    billRate: '',
    loadFactor: '',
    subVendorRate: '',
  });
  const [rateError, setRateError] = useState<string | null>(null);

  const canWriteExpenses = props.session.permissions.includes('time:write');

  const { data, error, reload } = useAsyncData(
    () => api.contractor(contractorId),
    [contractorId],
    'Could not load contractor.',
  );

  const filteredWeeks =
    data !== null
      ? data.weeks.filter(
          (w) =>
            search === '' || shortDate(w.weekEnding).toLowerCase().includes(search.toLowerCase()),
        )
      : [];

  const accessor = useCallback((row: ContractorWeek, key: string): unknown => {
    const map: Record<string, unknown> = {
      weekEnding: row.weekEnding,
      payRate: row.payRate,
      loadFactor: row.loadFactor,
      loadedCost: row.loadedCostPerHour,
      billRate: row.billRate,
      gpPerHour: row.grossProfitPerHour,
      hours: row.hours,
      expenses: row.expenses,
      subVendor: row.subVendorCost,
      grossProfit: row.grossProfit,
      billed: row.billed,
    };
    return map[key];
  }, []);

  const { sorted, sortKey, sortDirection, onSort } = useSortedRows(filteredWeeks, accessor);

  if (error !== null) {
    return (
      <Shell {...props} title="Contractor">
        <Card className="px-5 py-8">
          <p className="text-sm text-caution">{error}</p>
        </Card>
      </Shell>
    );
  }

  if (data === null) {
    return (
      <Shell {...props} title="Contractor">
        <Card>
          <Spinner label="Loading contractor history…" />
        </Card>
      </Shell>
    );
  }

  const showsRates = data.weeks.some((w) => w.payRate !== null);
  const totalBilled = data.weeks.reduce((s, w) => s + Number(w.billed), 0).toFixed(4);
  const totalGp = data.weeks.reduce((s, w) => s + Number(w.grossProfit), 0).toFixed(4);
  const totalHours = data.weeks.reduce((s, w) => s + Number(w.hours), 0).toFixed(4);

  return (
    <Shell {...props} title={data.name}>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => props.navigate('')}
          className="text-xs text-ink-faint hover:text-ink transition-colors"
        >
          ← Back to operations
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-baseline gap-4">
        <h1 className="text-xl font-semibold text-ink">{data.name}</h1>
        <span className="rounded-md bg-surface-overlay px-2 py-0.5 text-xs font-medium text-ink-muted">
          {data.workerType === 'contractor_1099' ? '1099' : 'W2'}
        </span>
        <span className="text-sm text-ink-muted">{data.clientName}</span>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Kpi
          label="Billed"
          value={moneyCompact(totalBilled)}
          exact={money(totalBilled)}
          tone="revenue"
          delay={0}
        />
        <Kpi
          label="Gross profit"
          value={moneyCompact(totalGp)}
          exact={money(totalGp)}
          tone="profit"
          delay={40}
        />
        <Kpi label="Hours" value={hours(totalHours)} delay={80} />
        <Kpi label="Weeks" value={String(data.weeks.length)} delay={120} />
        <Kpi
          label="GP margin"
          value={marginPercent(totalGp, totalBilled)}
          tone="profit"
          delay={160}
        />
      </div>

      {data.weeks.length > 1 && (
        <Card className="mt-4" delay={200}>
          <CardHeader title="Billed and gross profit" subtitle="Week by week" />
          <RevenueArea
            points={data.weeks.map((w) => ({
              label: w.weekEnding,
              billed: w.billed,
              grossProfit: w.grossProfit,
            }))}
          />
        </Card>
      )}

      <Card className="mt-4" delay={240}>
        <CardHeader
          title="Rate history"
          subtitle="Effective-dated rate changes"
          aside={
            <button
              type="button"
              onClick={() => {
                const opening = !showRateForm;
                setShowRateForm(opening);
                if (opening) {
                  api
                    .getWorkerLoadFactor(contractorId)
                    .then((resp) => {
                      if (resp.loadFactor) {
                        setRateForm((prev) =>
                          prev.loadFactor === '' ? { ...prev, loadFactor: resp.loadFactor! } : prev,
                        );
                      }
                    })
                    .catch(() => {
                      /* best-effort pre-fill */
                    });
                }
              }}
              className="rounded-lg border border-profit bg-profit/10 px-3 py-1.5 text-xs font-medium text-profit hover:bg-profit/20 transition-colors"
            >
              {showRateForm ? 'Cancel' : '+ Add Rate'}
            </button>
          }
        />
        {showRateForm && (
          <div className="px-4 pb-3 border-b border-edge">
            <form
              className="flex items-end gap-3 flex-wrap"
              onSubmit={async (e) => {
                e.preventDefault();
                setRateError(null);
                try {
                  await api.addRate(contractorId, {
                    assignmentId: data.assignmentId,
                    effectiveFrom: rateForm.effectiveFrom,
                    payRate: rateForm.payRate,
                    billRate: rateForm.billRate,
                    loadFactor: rateForm.loadFactor,
                    subVendorRate: rateForm.subVendorRate || undefined,
                  });
                  setShowRateForm(false);
                  setRateForm({
                    effectiveFrom: '',
                    payRate: '',
                    billRate: '',
                    loadFactor: '',
                    subVendorRate: '',
                  });
                  reload();
                } catch (err) {
                  setRateError(err instanceof Error ? err.message : 'Failed to add rate');
                }
              }}
            >
              <label className="flex flex-col gap-1 text-xs text-ink-muted">
                Effective from
                <input
                  type="date"
                  required
                  value={rateForm.effectiveFrom}
                  onChange={(e) => setRateForm({ ...rateForm, effectiveFrom: e.target.value })}
                  className="rounded-lg border border-edge bg-surface-raised px-2 py-1.5 text-sm text-ink"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-ink-muted">
                Pay rate
                <input
                  type="number"
                  step="0.01"
                  required
                  value={rateForm.payRate}
                  onChange={(e) => setRateForm({ ...rateForm, payRate: e.target.value })}
                  className="w-24 rounded-lg border border-edge bg-surface-raised px-2 py-1.5 text-sm text-ink text-right font-mono"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-ink-muted">
                Bill rate
                <input
                  type="number"
                  step="0.01"
                  required
                  value={rateForm.billRate}
                  onChange={(e) => setRateForm({ ...rateForm, billRate: e.target.value })}
                  className="w-24 rounded-lg border border-edge bg-surface-raised px-2 py-1.5 text-sm text-ink text-right font-mono"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-ink-muted">
                Load factor
                <input
                  type="number"
                  step="0.001"
                  required
                  value={rateForm.loadFactor}
                  onChange={(e) => setRateForm({ ...rateForm, loadFactor: e.target.value })}
                  className="w-24 rounded-lg border border-edge bg-surface-raised px-2 py-1.5 text-sm text-ink text-right font-mono"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-ink-muted">
                Sub-vendor $/hr
                <input
                  type="number"
                  step="0.01"
                  value={rateForm.subVendorRate}
                  onChange={(e) => setRateForm({ ...rateForm, subVendorRate: e.target.value })}
                  placeholder="0"
                  className="w-24 rounded-lg border border-edge bg-surface-raised px-2 py-1.5 text-sm text-ink text-right font-mono"
                />
              </label>
              <button
                type="submit"
                className="rounded-lg border border-profit bg-profit/10 px-4 py-1.5 text-sm font-medium text-profit hover:bg-profit/20 transition-colors"
              >
                Save
              </button>
            </form>
            {rateError && <p className="mt-2 text-xs text-red-600">{rateError}</p>}
          </div>
        )}
        {data.rates.length > 0 ? (
          <Table
            head={[
              { label: 'Effective from' },
              { label: 'Effective to' },
              { label: 'Pay rate', align: 'right' },
              { label: 'Load', align: 'right' },
              { label: 'Bill rate', align: 'right' },
            ]}
          >
            {data.rates.map((r, i) => (
              <Row key={i}>
                <Cell>{shortDate(r.effectiveFrom)}</Cell>
                <Cell muted>{r.effectiveTo !== null ? shortDate(r.effectiveTo) : 'current'}</Cell>
                <Cell align="right" mono>
                  {rate(r.payRate)}
                </Cell>
                <Cell align="right" mono muted>
                  {Number(r.loadFactor).toFixed(3)}
                </Cell>
                <Cell align="right" mono>
                  {rate(r.billRate)}
                </Cell>
              </Row>
            ))}
          </Table>
        ) : (
          <p className="px-4 py-3 text-sm text-ink-muted">
            No rates configured for this contractor.
          </p>
        )}
      </Card>

      <Card className="mt-4" delay={280}>
        <CardHeader
          title="Weekly breakdown"
          subtitle="The same columns as the gross profit workbook contractor tab"
          aside={
            <div className="flex items-center gap-3">
              <div className="w-48">
                <SearchBar value={search} onChange={setSearch} placeholder="Filter by week…" />
              </div>
              <ExportButton
                data={sorted.map((w) => ({
                  weekEnding: shortDate(w.weekEnding),
                  ...(showsRates
                    ? {
                        payRate: rate(w.payRate),
                        loadFactor: w.loadFactor !== null ? Number(w.loadFactor).toFixed(3) : '',
                        loadedCost: rate(w.loadedCostPerHour),
                      }
                    : {}),
                  billRate: rate(w.billRate),
                  gpPerHour: rate(w.grossProfitPerHour),
                  hours: hours(w.hours),
                  expenses: w.expenses !== '0.0000' && w.expenses !== '0' ? money(w.expenses) : '',
                  subVendor:
                    w.subVendorCost !== '0.0000' && w.subVendorCost !== '0'
                      ? money(w.subVendorCost)
                      : '',
                  grossProfit: money(w.grossProfit),
                  billed: money(w.billed),
                }))}
                filename={`contractor-${data.name.toLowerCase().replace(/\s+/g, '-')}-weeks`}
                headers={[
                  { key: 'weekEnding', label: 'Week Ending' },
                  ...(showsRates
                    ? [
                        { key: 'payRate', label: 'Pay Rate' },
                        { key: 'loadFactor', label: 'Load Factor' },
                        { key: 'loadedCost', label: 'Loaded Cost' },
                      ]
                    : []),
                  { key: 'billRate', label: 'Bill Rate' },
                  { key: 'gpPerHour', label: 'GP/h' },
                  { key: 'hours', label: 'Hours' },
                  { key: 'expenses', label: 'Expenses' },
                  { key: 'subVendor', label: 'Sub Vendor' },
                  { key: 'grossProfit', label: 'Gross Profit' },
                  { key: 'billed', label: 'Billed' },
                ]}
              />
            </div>
          }
        />
        <Table
          head={[
            { label: 'Week ending', sortKey: 'weekEnding' },
            ...(showsRates
              ? [
                  { label: 'Pay', align: 'right' as const, sortKey: 'payRate' },
                  { label: 'Load', align: 'right' as const, sortKey: 'loadFactor' },
                  { label: 'Loaded cost', align: 'right' as const, sortKey: 'loadedCost' },
                ]
              : []),
            { label: 'Bill', align: 'right', sortKey: 'billRate' },
            { label: 'GP/h', align: 'right', sortKey: 'gpPerHour' },
            { label: 'Hours', align: 'right', sortKey: 'hours' },
            { label: 'Expenses', align: 'right', sortKey: 'expenses' },
            { label: 'Sub vendor', align: 'right', sortKey: 'subVendor' },
            { label: 'GP (week)', align: 'right', sortKey: 'grossProfit' },
            { label: 'Billed', align: 'right', sortKey: 'billed' },
          ]}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={onSort}
        >
          {sorted.map((w) => (
            <Row key={`${w.assignmentId}-${w.weekEnding}`}>
              <Cell>{shortDate(w.weekEnding)}</Cell>
              {showsRates && (
                <>
                  <Cell align="right" mono>
                    {rate(w.payRate)}
                  </Cell>
                  <Cell align="right" mono muted>
                    {w.loadFactor !== null ? Number(w.loadFactor).toFixed(3) : '—'}
                  </Cell>
                  <Cell align="right" mono muted>
                    {rate(w.loadedCostPerHour)}
                  </Cell>
                </>
              )}
              <Cell align="right" mono>
                {rate(w.billRate)}
              </Cell>
              <Cell align="right" mono>
                {rate(w.grossProfitPerHour)}
              </Cell>
              <Cell align="right" mono>
                {hours(w.hours)}
              </Cell>
              <Cell align="right" mono muted>
                <ExpenseCell week={w} canWrite={canWriteExpenses} onSaved={reload} />
              </Cell>
              <Cell align="right" mono muted>
                {w.subVendorCost !== '0.0000' && w.subVendorCost !== '0'
                  ? money(w.subVendorCost)
                  : ''}
              </Cell>
              <Cell align="right" mono>
                <span className="text-profit">{money(w.grossProfit)}</span>
              </Cell>
              <Cell align="right" mono>
                {money(w.billed)}
              </Cell>
            </Row>
          ))}
          <Row>
            <Cell>
              <span className="font-semibold text-ink">Total</span>
            </Cell>
            {showsRates && (
              <>
                <Cell align="right" mono>
                  {''}
                </Cell>
                <Cell align="right" mono>
                  {''}
                </Cell>
                <Cell align="right" mono>
                  {''}
                </Cell>
              </>
            )}
            <Cell align="right" mono>
              {''}
            </Cell>
            <Cell align="right" mono>
              {''}
            </Cell>
            <Cell align="right" mono>
              <span className="font-semibold">{hours(totalHours)}</span>
            </Cell>
            <Cell align="right" mono>
              {''}
            </Cell>
            <Cell align="right" mono>
              {''}
            </Cell>
            <Cell align="right" mono>
              <span className="font-semibold text-profit">{money(totalGp)}</span>
            </Cell>
            <Cell align="right" mono>
              <span className="font-semibold">{money(totalBilled)}</span>
            </Cell>
          </Row>
        </Table>
      </Card>

      {data.assignmentStart !== null && (
        <Card className="mt-4" delay={320}>
          <CardHeader title="Assignment" />
          <div className="grid grid-cols-2 gap-6 px-5 py-4 sm:grid-cols-4">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-ink-faint">
                Engagement Start
              </p>
              <p className="mt-1 text-sm text-ink">{shortDate(data.assignmentStart)}</p>
            </div>
            <div>
              <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-ink-faint">
                End
              </p>
              <p className="mt-1 text-sm text-ink">
                {data.assignmentEnd !== null ? shortDate(data.assignmentEnd) : 'Active'}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-ink-faint">
                Status
              </p>
              <p className="mt-1 text-sm capitalize text-ink">
                {data.assignmentStatus.replace('_', ' ')}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-ink-faint">
                Customer
              </p>
              <p className="mt-1 text-sm text-ink">{data.clientName}</p>
            </div>
          </div>
        </Card>
      )}

      <BenefitsCard
        workerId={data.id}
        holidayDays={data.holidayDays}
        vacationDays={data.vacationDays}
        onSaved={reload}
      />

      {data.verificationNotes && data.verificationNotes.length > 0 && (
        <Card className="mt-4" delay={360}>
          <CardHeader title="Verification Notes" />
          <div className="px-4 pb-4 space-y-2">
            {data.verificationNotes.map((n, i) => (
              <div key={i} className="rounded-lg border border-edge px-3 py-2 text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span
                    className={`font-medium ${
                      n.status === 'verified' || n.status === 'auto_verified'
                        ? 'text-profit'
                        : n.status === 'rejected'
                          ? 'text-red-600'
                          : 'text-ink-muted'
                    }`}
                  >
                    {n.status.charAt(0).toUpperCase() + n.status.slice(1).replace(/_/g, ' ')} — Week
                    ending {shortDate(n.weekEnding)}
                  </span>
                  <span className="text-[10px] text-ink-faint">
                    {n.verifiedBy ?? 'System'}
                    {n.verifiedAt ? ` · ${new Date(n.verifiedAt).toLocaleString()}` : ''}
                  </span>
                </div>
                {n.note && <p className="text-ink-muted">{n.note}</p>}
              </div>
            ))}
          </div>
        </Card>
      )}
    </Shell>
  );
}

function periodIsLocked(status: string): boolean {
  return status === 'closed' || status === 'submitted';
}

function ExpenseCell({
  week,
  canWrite,
  onSaved,
}: {
  week: ContractorWeek;
  canWrite: boolean;
  onSaved: () => void;
}) {
  const locked = periodIsLocked(week.periodStatus);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(week.expenses);
  const [error, setError] = useState<string | null>(null);

  const display =
    week.expenses !== '0.0000' && week.expenses !== '0' && week.expenses !== '0.00'
      ? money(week.expenses)
      : '';

  if (!canWrite || locked) {
    return <>{display}</>;
  }

  const save = () => {
    const trimmed = value.trim();
    setError(null);
    if (trimmed === '' || trimmed === '0' || trimmed === '0.00') {
      if (week.expenseId) {
        void api
          .deleteExpense(week.expenseId)
          .then(() => {
            setEditing(false);
            onSaved();
          })
          .catch((cause: unknown) => {
            setError(cause instanceof Error ? cause.message : 'Could not clear expense');
          });
        return;
      }
      setEditing(false);
      setValue(week.expenses);
      return;
    }
    if (trimmed === week.expenses) {
      setEditing(false);
      return;
    }
    void api
      .saveExpense({
        assignmentId: week.assignmentId,
        payPeriodId: week.payPeriodId,
        amount: trimmed,
      })
      .then(() => {
        setEditing(false);
        onSaved();
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : 'Could not save expense');
      });
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          setValue(week.expenses === '0.0000' || week.expenses === '0' ? '' : week.expenses);
          setEditing(true);
        }}
        className="min-w-[4rem] rounded px-1 py-0.5 text-right hover:bg-surface-overlay"
        title="Edit expense"
      >
        {display === '' ? <span className="text-ink-faint">Add</span> : display}
      </button>
    );
  }

  return (
    <div className="flex flex-col items-end">
      <input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') {
            setValue(week.expenses);
            setEditing(false);
          }
        }}
        className="w-20 rounded border border-profit bg-surface-raised px-1.5 py-0.5 text-right font-mono text-sm"
      />
      {error && <span className="mt-0.5 text-[10px] text-red-600">{error}</span>}
    </div>
  );
}

/* ── Benefits Card ──────────────────────────────────────────────────── */

function BenefitsCard({
  workerId,
  holidayDays,
  vacationDays,
  onSaved,
}: {
  workerId: string;
  holidayDays: number | null;
  vacationDays: number | null;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [hDays, setHDays] = useState(String(holidayDays ?? ''));
  const [vDays, setVDays] = useState(String(vacationDays ?? ''));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [loadFactor, setLoadFactor] = useState<string | null>(null);

  const fetchLoadFactor = useCallback(async () => {
    try {
      const resp = await api.getWorkerLoadFactor(workerId);
      setLoadFactor(resp.loadFactor);
    } catch {
      setLoadFactor(null);
    }
  }, [workerId]);

  const startEdit = () => {
    setHDays(String(holidayDays ?? ''));
    setVDays(String(vacationDays ?? ''));
    setEditing(true);
    setError(null);
    void fetchLoadFactor();
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.updateWorkerBenefits(workerId, {
        holidayDays: hDays === '' ? null : Number(hDays),
        vacationDays: vDays === '' ? null : Number(vDays),
      });
      setEditing(false);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mt-4" delay={340}>
      <CardHeader
        title="Benefits & Burden"
        subtitle={
          holidayDays !== null || vacationDays !== null
            ? `${String(holidayDays ?? 0)} holiday days, ${String(vacationDays ?? 0)} vacation days`
            : 'Not configured'
        }
        aside={
          !editing ? (
            <button
              type="button"
              className="rounded-lg border border-profit bg-profit/10 px-3 py-1.5 text-xs font-medium text-profit hover:bg-profit/20 transition-colors"
              onClick={startEdit}
            >
              {holidayDays !== null ? 'Edit' : 'Configure'}
            </button>
          ) : undefined
        }
      />
      {editing && (
        <div className="space-y-3 px-4 pb-4">
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex items-end gap-4">
            <label className="flex flex-col gap-1 text-xs text-ink-muted">
              Holiday Days
              <input
                type="number"
                min="0"
                max="30"
                value={hDays}
                onChange={(e) => setHDays(e.target.value)}
                className="w-20 rounded-lg border border-edge bg-surface-raised px-2 py-1.5 text-sm text-ink text-right font-mono"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-ink-muted">
              Vacation / PTO Days
              <input
                type="number"
                min="0"
                max="50"
                value={vDays}
                onChange={(e) => setVDays(e.target.value)}
                className="w-20 rounded-lg border border-edge bg-surface-raised px-2 py-1.5 text-sm text-ink text-right font-mono"
              />
            </label>
            {loadFactor !== null && (
              <div className="text-xs text-ink-muted">
                Computed load factor:{' '}
                <span className="font-mono font-semibold text-ink">{loadFactor}</span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded-lg border border-profit bg-profit/10 px-4 py-1.5 text-xs font-medium text-profit hover:bg-profit/20 transition-colors disabled:opacity-50"
              onClick={() => void save()}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="rounded-lg border border-edge px-4 py-1.5 text-xs font-medium text-ink-muted hover:bg-surface-alt transition-colors"
              onClick={() => setEditing(false)}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}
